import type { FC } from 'react';
import { FeedbackType } from '../model/Feedback';
import {
    BodyText,
    Box,
    Button,
    Icon,
    IconButton,
    PopoverTemplate,
    TextAreaField,
    type IconType,
} from '@placer-ui/components';
import React, { useCallback } from 'react';
import type { IConversationRun } from '../model/ConversationRun';
import { useConversationRun } from './hooks/useConversationRun';
import styles from './feedbackContent.module.scss';
import { noop } from 'lodash';

export const FeedbackContent: FC<{
    run: IConversationRun;
    openedFeedback: FeedbackType | undefined;
    setOpenedFeedback: (type: FeedbackType | undefined) => void;
    feedbackType: FeedbackType;
}> = (props) => {
    const { run, feedbackType, openedFeedback, setOpenedFeedback } = props;
    const [content, setContent] = React.useState('');
    const [isThankYouState, setIsThankYouState] = React.useState(false);
    const { type, sendFeedback } = useConversationRun(run);
    const iconType: IconType = feedbackType === FeedbackType.Positive ? 'ThumbsUp' : 'ThumbsDown';
    const testId: string = `${feedbackType}-feedback-content-${run.getId()}`;

    const isSelected = type === feedbackType;
    const handleFeedbackButtonClick = useCallback(() => {
        setOpenedFeedback(feedbackType);
        sendFeedback(feedbackType);
    }, [setOpenedFeedback, feedbackType, sendFeedback]);

    const handleSubmitFeedback = useCallback(() => {
        sendFeedback(feedbackType, content);
        setIsThankYouState(true);
        setTimeout(() => {
            setIsThankYouState(false);
            setOpenedFeedback(undefined);
        }, 2000);
    }, [setOpenedFeedback, feedbackType, content, sendFeedback]);

    let feedbackContent = (
        <>
            <Box className={styles.feedbackResponse}>
                <BodyText>Why did you choose this rating?</BodyText>
                <TextAreaField
                    placeholder="Provide additional feedback"
                    initialHeight={64}
                    value={content}
                    onChange={setContent}
                    testId={`feedback-textarea-${run.getId()}-${feedbackType}`}
                />
            </Box>
            <Button
                size="small"
                onClick={handleSubmitFeedback}
                testId={`feedback-submit-button-${run.getId()}-${feedbackType}`}
            >
                Submit
            </Button>
        </>
    );

    if (isThankYouState) {
        feedbackContent = (
            <BodyText testId={`feedback-thank-you-${run.getId()}-${feedbackType}`}>
                Thank you for your feedback!
            </BodyText>
        );
    }

    return (
        <PopoverTemplate
            backgroundColor="form"
            open={openedFeedback === feedbackType}
            onOpenChange={(isOpened) => {
                setOpenedFeedback(isOpened ? feedbackType : undefined);
            }}
            header={
                <PopoverTemplate.Header onCloseSequence={noop} title="Thanks for your rating" />
            }
            align="start"
            anchor={
                <IconButton
                    Icon={<Icon size={16} type={iconType} />}
                    aria-label="Send negative feedback"
                    variant={isSelected ? 'secondary-blue' : 'ghost'}
                    onClick={isSelected ? noop : handleFeedbackButtonClick}
                    testId={`feedback-icon-button-${run.getId()}-${feedbackType}`}
                />
            }
        >
            <div data-testid={testId} className={styles.contentContainer}>{feedbackContent}</div>
        </PopoverTemplate>
    );
};
